import { useEffect, useRef, useState } from "react";
import { getProductDetails, ProductData } from "../../contentfulService";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

type GridItem = {
    id: string;
    title: string;
    imageUrl: string;
    description?: any;
};

export default function ProductsModule() {
    const [products, setProducts] = useState<ProductData[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);

    const stripRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    /* ================= DATA ================= */
    useEffect(() => {
        getProductDetails().then(setProducts);
    }, []);

    const items: GridItem[] = products.slice(0, 5).map((p) => ({
        id: p.id,
        title: p.title ?? "No Title",
        imageUrl: p.imageUrl ?? "/placeholder.jpg",
        description: p.description,
    }));

    /* ================= SCROLL STATE ================= */
    const updateScrollState = () => {
        const el = stripRef.current;
        if (!el) return;

        setCanScrollLeft(el.scrollLeft > 2);
        setCanScrollRight(
            el.scrollLeft + el.clientWidth < el.scrollWidth - 2
        );
    };

    useEffect(() => {
        updateScrollState();
    }, [items]);

    const scroll = (dir: "left" | "right") => {
        const el = stripRef.current;
        if (!el) return;

        el.scrollBy({
            left: dir === "left" ? -el.clientWidth * 0.8 : el.clientWidth * 0.8,
            behavior: "smooth",
        });
    };

    /* ================= SAFETY ================= */
    if (!items.length) {
        return <section className="bg-[--color-bg-main] min-h-[500px]" />;
    }

    const activeItem = items[activeIndex] ?? items[0];

    return (
        <section id="product" className="bg-[--color-bg-main] overflow-hidden pt-[64px]">
            {/* ================= Todo Contenful umsetzen ================= */}
            <div className="bg-black">
                            <h1 className="text-white text-center text-3xl md:text-3xl tracking-tight text-textPrimary max-w-2xl mx-auto px-8 py-24 grid items-center">
                AK Origins, Ihr Partner für grüne Kaffeebohnen aus Uganda – direkt vom Ursprung geliefert.</h1>
            </div>

            {/* ================= PRODUCT STRIP ================= */}
            <div className="relative w-screen left-1/2 -translate-x-1/2">
                {/* LEFT ARROW */}
                {canScrollLeft && (
                    <button
                        onClick={() => scroll("left")}
                        aria-label="Scroll left"
                        className="
              absolute left-4 top-1/2 -translate-y-1/2 z-20
              text-gray-400 text-3xl
              hover:text-gray-600
              transition-colors duration-300
              select-none
            "
                    >
                        ‹
                    </button>
                )}

                {/* RIGHT ARROW */}
                {canScrollRight && (
                    <button
                        onClick={() => scroll("right")}
                        aria-label="Scroll right"
                        className="
              absolute right-4 top-1/2 -translate-y-1/2 z-20
              text-gray-400 text-3xl
              hover:text-gray-600
              transition-colors duration-300
              select-none
            "
                    >
                        ›
                    </button>
                )}

                {/* SCROLL CONTAINER */}
                <div
                    ref={stripRef}
                    onScroll={updateScrollState}
                    className="overflow-x-auto no-scrollbar"
                >
                    <div className="flex flex-nowrap h-[220px] min-w-max">
                        {items.map((item, i) => (
                            <button
                                key={item.id}
                                onMouseEnter={() => setActiveIndex(i)}
                                onClick={() => setActiveIndex(i)}
                                className="
                  relative
                  flex-shrink-0
                  w-[70vw]
                  sm:w-[50vw]
                  md:w-[33vw]
                  lg:w-[20vw]
                  h-full
                  overflow-hidden
                  focus:outline-none
                "
                            >
                                <img
                                    src={item.imageUrl}
                                    alt={item.title}
                                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                />

                                <div
                                    className={`
                    absolute inset-0 flex items-center justify-center
                    transition-colors duration-300
                    ${
                                        activeIndex === i
                                            ? "bg-black/40"
                                            : "bg-black/0 hover:bg-black/40"
                                    }
                  `}
                                >
                  <span className="bg-black text-white text-sm px-5 py-2 rounded-full">
                    {item.title}
                  </span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ================= CONTENT ================= */}
            <div
                className="
          min-h-[500px]
          max-w-7xl
          mx-auto
          px-8
          py-24
          grid
          md:grid-cols-2
          gap-20
          items-center
          md:items-start
        "
            >
                {/* LEFT */}
                <div className="flex flex-col justify-center h-full text-center md:text-center">
                    <p className="uppercase tracking-widest text-xs text-textMuted mb-4 text-gray-400">
                        MANIFESTO
                    </p>

                    <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-textPrimary">
                    {activeItem.title}
                    </h2>
                </div>

                {/* RIGHT */}
                {activeItem.description && (
                    <div className="w-full max-w-[520px] mx-auto md:ml-auto">
                        <div className="text-gray-600 space-y-4 text-center md:text-left">
                            {documentToReactComponents(activeItem.description)}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
